<template>
  <div class="tabs">
    <div v-if="!loading">
      <div
        v-if="isMobile"
        class="mobile-tabs"
      >
        <div
          v-for="(title, index) in visibleTabs"
          :key="index"
          :class="{
            'tab-item': true,
            selected: title == selectedTitle,
            tabs_error: getTabErrorStatus(title)
          }"
          @click="changeTab(title, getOriginalIndex(title))"
        >
          {{ title }}
        </div>
      </div>

      <div
        v-else
        class="tabs_header_wrapper"
      >
        <Swiper
          class="swiper"
          :style="cssVars"
          :navigation="true"
          :slides-per-view="slidesPerView"
          :hash-navigation="{
            watchState: true
          }"
          :modules="modules"
        >
          <SwiperSlide
            v-for="(title, index) in visibleTabs"
            :key="index"
          >
            <div
              :class="{
                'tab-item': true,
                selected: title == selectedTitle,
                tabs_error: getTabErrorStatus(title)
              }"
              @click="changeTab(title, getOriginalIndex(title))"
            >
              {{ title }}
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
    <div class="tabs_info">
      <slot />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { computed, provide, ref, watch } from "vue";
import { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/vue";
import { useRoute } from "vue-router";

import "swiper/css";
import "swiper/css/navigation";

export default {
  name: "AuTabView",
  components: {
    Swiper,
    SwiperSlide
  },

  props: {
    activeTab: {
      type: String,
      default: null
    },
    paddingSlide: {
      type: String,
      default: "20px"
    }
  },

  setup(props, { slots }) {
    const route = useRoute();

    const filteredSLots = slots.default().filter(tab => tab.props);

    const tabTitles = ref(filteredSLots.map((tab) => tab?.props?.title));

    const tabErrors = ref(
      filteredSLots.map((tab) => ({
        title: tab.props.title,
        status: tab.props.showError === undefined ? false : tab.props.showError
      }))
    );

    const hiddens = ref(
      filteredSLots.map((tab) => ({
        title: tab.props.title,
        status: tab.props.hidden === undefined ? false : tab.props.hidden
      }))
    );

    const visibleTabs = computed(() => {
      return tabTitles.value.filter(
        (title, index) => !hiddens.value[index].status
      );
    });

    let selectedTitle = ref(tabTitles.value[0]);

    if (route.query?.selectedTitle) {
      let index = _.findIndex(
        tabTitles.value,
        (item) => item == route.query?.selectedTitle
      );
      if (index !== -1) {
        selectedTitle = ref(tabTitles.value[index]);
      }
    }

    watch(
      () => props.activeTab,
      (newActiveTab) => {
        if (newActiveTab && tabTitles.value.includes(newActiveTab)) {
          selectedTitle.value = newActiveTab;
        }
      }
    );

    const getTabErrorStatus = (title) => {
      const index = tabTitles.value.indexOf(title);
      return index !== -1 ? tabErrors.value[index]?.status : false;
    };

    const getOriginalIndex = (title) => {
      return tabTitles.value.indexOf(title);
    };

    provide("selectedTitle", selectedTitle);
    provide("tabErrors", tabErrors);
    provide("hiddens", hiddens);

    return {
      selectedTitle,
      tabTitles,
      visibleTabs,
      tabErrors,
      hiddens,
      getTabErrorStatus,
      getOriginalIndex,
      modules: [Navigation]
    };
  },

  data() {
    return {
      slidesPerView: 0,
      loading: true,
      isMobile: false
    };
  },

  computed: {
    cssVars() {
      return {
        "--paddingSlide": this.paddingSlide
      };
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.checkMobile();
      window.addEventListener("resize", this.checkMobile);

      const doc = document.querySelector(".tabs");
      if (doc && !this.isMobile) {
        const tabWidth = 150;
        const availableWidth = doc.offsetWidth;
        this.slidesPerView = Math.min(
          this.visibleTabs.length,
          Math.max(1, Math.floor((availableWidth - 40) / tabWidth))
        );
      }
      this.loading = false;
    });
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
  },

  methods: {
    changeTab(t, i) {
      this.selectedTitle = t;
      this.$emit("change", t);
      this.$emit("changeIndex", i);
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 500;
    }
  }
};
</script>

<style scoped lang="scss">
.tabs_header_wrapper {
  margin: 0;
  padding: 0;
  width: 100%;
}

.swiper {
  margin-left: 0px;
  width: 100%;

  &-slide {
    cursor: pointer;
    width: auto !important;
    flex-shrink: 0;
  }
}

.mobile-tabs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: 10px;
}

.tab-item {
  cursor: pointer;
  background: var(--card-color);
  color: var(--color-50);
  text-align: center;
  padding: 0.625rem 1.25rem;
  border-radius: 8px 8px 0px 0px;
  transition: 0.2s ease-in-out;
  white-space: nowrap;
  display: block;
  margin-right: 2px;

  &:hover,
  &:focus {
    color: var(--text-color);

    &.tabs_error {
      color: red;
    }
  }

  &.selected {
    color: var(--text-color);
    background: var(--card-color);
    position: relative;
    z-index: 2;

    &.tabs_error {
      color: red;
    }
  }

  &.tabs_error {
    color: #ffb7af;
  }

  @media screen and (max-width: 500px) {
    margin-right: 0px;
    border-radius: 8px !important;
    margin-bottom: 0;
    width: 100%;
    box-sizing: border-box;
  }
}

.tabs_info {
  background: var(--card-color);
  border-radius: 0 10px 10px 10px;
  position: relative;
  z-index: 1;

  @media screen and (max-width: 500px) {
    border-radius: 10px;
  }
}
</style>
